import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ocFetch, OcApiError } from "@/lib/oc-api";
import { OcAuthPageShell, ocAuthInputCls, ocAuthButtonCls } from "@/components/shared/OcAuthPageShell";

function ocApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof OcApiError) {
    const body = err.body as { error?: unknown } | undefined;
    if (body && typeof body.error === "string" && body.error) return body.error;
  }
  return fallback;
}

export function OcForgotPasswordPage() {
  const location = useLocation();
  const prefill = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(prefill);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await ocFetch("/tenant/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (err: unknown) {
      setError(ocApiErrorMessage(err, "Couldn't send the reset email. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OcAuthPageShell title="Forgot your password?" subtitle="We'll email you a link to choose a new one">
      {sent ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
            If an account exists for <strong>{email.trim()}</strong>, a password reset link is on its way.
            The link expires in 1 hour.
          </div>
          <p className="text-sm text-slate-500">
            Didn't get it? Check your spam folder, or ask your administrator to confirm which email
            address is on your account.
          </p>
          <Link to="/login" className="block text-center text-sm font-medium text-oc-teal-600 hover:text-oc-teal-700">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          <div className="space-y-1">
            <label htmlFor="oc-forgot-email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="oc-forgot-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={ocAuthInputCls}
              placeholder="you@institution.edu"
            />
          </div>
          <button type="submit" disabled={submitting} className={ocAuthButtonCls}>
            {submitting ? "Sending…" : "Send reset link"}
          </button>
          <Link to="/login" className="block text-center text-sm font-medium text-oc-teal-600 hover:text-oc-teal-700">
            Back to sign in
          </Link>
        </form>
      )}
    </OcAuthPageShell>
  );
}
